// src/pages/userInfoPage.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '@/config';
import BaseContainer from '@/components/baseContainer';
import { fetchPurposeOptions, fetchIndustryOptions, MasterOption } from '@/data';

type UserFromAPI = {
  email: string;
  last_name_kanji: string;
  first_name_kanji: string;
  last_name_alpha: string;
  first_name_alpha: string;
  birthday: string;
  gender: string; // 'male' | 'female' | 'other' など
  zip_current: string;
  address_current: string;
  amc?: string;
  phone: string;
  job: string;
  jobLabel?: { value: string; label: string }; // APIにあれば活用
  industry?: string;        // コード（'99' でその他）
  industry_other?: string;  // '99' のときの自由記入
  purpose: string;          // CSV
  purpose_detail: string;
  purpose_other?: string;   // 目的のその他自由記入
};

async function fetchWithTimeout(input: RequestInfo | URL, init: RequestInit = {}, ms = 15000) {
  const ctrl = new AbortController();
  const id = setTimeout(() => ctrl.abort(), ms);
  try { return await fetch(input, { ...init, signal: ctrl.signal }); }
  finally { clearTimeout(id); }
}

const MAIL_CHANGE_PATH = '/email/change'; // ルーティングに合わせて調整

export default function UserEditPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [u, setU] = useState<UserFromAPI | null>(null);

  // マスター
  const [purposeOptions, setPurposeOptions] = useState<MasterOption[]>([]);
  const [industryOptions, setIndustryOptions] = useState<MasterOption[]>([]);

  // 目的（CSV→ラベル）※ 99 は「その他（自由記入）」で表示
  const purposeText = React.useMemo(() => {
    const codes = (u?.purpose ?? '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);

    if (!codes.length) return '-';

    const other = (u?.purpose_other ?? '').trim();

    if (!purposeOptions.length) {
      return codes
        .map(c => (c === '99' ? (other ? `その他（${other}）` : 'その他') : c))
        .join('、');
    }

    const dict = new Map(purposeOptions.map(o => [o.value, o.label]));
    return codes
      .map(c => {
        if (c === '99') {
          const base = dict.get('99') ?? 'その他';
          return other ? `${base}（${other}）` : base;
        }
        return dict.get(c) ?? c;
      })
      .join('、');
  }, [u?.purpose, u?.purpose_other, purposeOptions]);

  // 職業（コード→ラベル）※ 99 は industry_other を表示
  const industryLabel = React.useMemo(() => {
    const code = (u?.industry ?? '').trim();
    if (!code) return '-';
    if (code === '99') {
      const other = (u?.industry_other ?? '').trim();
      return other || 'その他';
    }
    if (!industryOptions.length) return code; // マスター未取得時のフォールバック
    const dict = new Map(industryOptions.map(o => [o.value, o.label]));
    return dict.get(code) ?? code;
  }, [u?.industry, u?.industry_other, industryOptions]);

  // データ取得
  useEffect(() => {
    (async () => {
      const token = localStorage.getItem('token');
      if (!token) { navigate('/login'); return; }
      try {
        const res = await fetch(`${API_BASE_URL}/v1/user/info`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error('failed to fetch user');
        const data: UserFromAPI = await res.json();
        setU(data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  // マスター取得
  useEffect(() => {
    (async () => {
      try {
        const [pOpts, iOpts] = await Promise.all([
          fetchPurposeOptions(),
          fetchIndustryOptions(),
        ]);
        setPurposeOptions(pOpts);
        setIndustryOptions(iOpts);
      } catch (e) {
        console.error('failed to load master options', e);
      }
    })();
  }, []);

  const handleEmailChange = () => {
    navigate(MAIL_CHANGE_PATH);
  };

  const genderJa = (g?: string) =>
    g === 'male' ? '男性' : g === 'female' ? '女性' : g ? 'その他' : '-';

  const show = (v?: string | null) => (v && String(v).trim().length ? v : '-');

  if (loading) return <div className="text-center mt-10">読み込み中...</div>;
  if (!u) return <div className="text-center mt-10">データを取得できませんでした</div>;

  return (
    <div className="py-16">
      <div className="flex flex-col gap-8 pt-8">

        {/* 基本情報 */}
        <BaseContainer>
          <div className="xmd:w-[80rem] mx-auto flex flex-col gap-6">
            <p className="m-form-title mb-2">基本情報</p>

            <div className="grid grid-cols-1 sm:grid-cols-[12rem,1fr] gap-x-8 gap-y-4 items-start">

              <div className="text-slate-500">メールアドレス</div>
              {/* メール欄だけボタンで遷移 */}
              <div className="flex items-center gap-3 min-w-0" key="email-row">
                <span className="font-medium truncate flex-1 min-w-0">
                  {show(u.email)}
                </span>
                <button
                  type="button"
                  className="m-btn fill !inline-flex !w-[120px] !min-w-[120px] !px-4 !py-2 shrink-0"
                  onClick={handleEmailChange}
                  aria-label="認証コードを送る"
                  title="認証コードを送る"
                >
                  変更
                </button>
              </div>

              <div className="text-slate-500">氏名</div>
              <div>{show(`${u.last_name_kanji} ${u.first_name_kanji}`.trim())}</div>

              <div className="text-slate-500">氏名（ローマ字）</div>
              <div>{show(`${u.last_name_alpha} ${u.first_name_alpha}`.trim())}</div>

              <div className="text-slate-500">生年月日</div>
              <div>{show(u.birthday)}</div>

              <div className="text-slate-500">性別</div>
              <div>{genderJa(u.gender)}</div>

              <div className="text-slate-500">職業形態</div>
              <div>{show(u.jobLabel?.label || u.job)}</div>

              <div className="text-slate-500">職業</div>
              <div>{industryLabel}</div>
            </div>
          </div>

          <br /><br />

          <div className="xmd:w-[80rem] mx-auto flex flex-col gap-6">
            {/* <p className="m-form-title mb-2">自治体の申請</p> */}
            <div className="grid grid-cols-1 sm:grid-cols-[12rem,1fr] gap-x-8 gap-y-4 items-start">
              <div className="text-slate-500">郵便番号</div>
              <div>{show(u.zip_current)}</div>

              <div className="text-slate-500">住所</div>
              <div>{show(u.address_current)}</div>

              <div className="text-slate-500">ANAマイレージクラブお客様番号</div>
              <div>{show(u.amc)}</div>

              <div className="text-slate-500">電話番号</div>
              <div>{show(u.phone)}</div>

              <div className="text-slate-500">二地域居住の目的</div>
              <div>{purposeText || '-'}</div>

              <div className="text-slate-500">具体的な目的</div>
              <div className="whitespace-pre-wrap">{show(u.purpose_detail)}</div>
            </div>
          </div>

          {/* 戻る／編集 */}
          <div className="mt-8 flex items-center justify-center gap-4">
            <button
              type="button"
              className="m-btn w-32 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => navigate('/mypage')}
            >
              キャンセル
            </button>
            <button
              type="button"
              className="m-btn fill w-32 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => navigate('/edit')}
            >
              編集
            </button>
          </div>
        </BaseContainer>
      </div>
    </div>
  );
}
